/**
 * The public Trust Centre (trust.pdpo.ai) — where a person reads an
 * organisation's privacy page and published policies, and uses the consent,
 * withdrawal and request links. Every customer-facing page links back to the
 * organisation's page there, so the URL shapes and host detection live here.
 *
 * Customer-facing pages offer the actions, not only the notice: the privacy
 * page (/p/<slug>), "request your data" (/dsar/<public_portal_token>) and
 * "report a concern" (/report/<public_portal_token>) — the same forms the
 * Trust Centre page itself links to — plus the signed-in customer portal.
 * `trustLinks()` mirrors public-pages `trustLinks()` so every surface builds
 * the same URLs. Never invent a URL: a link whose token is missing is null.
 *
 * Hosts:
 *   trust.pdpo.ai            → public landing page + organisation directory
 *   <slug>.trust.pdpo.ai     → that organisation's hosted privacy centre
 * Additional bases (e.g. a vanity domain tree) can be added without code
 * changes elsewhere — DNS + this list are the only switches.
 */

export const TRUST_CENTRE_URL = 'https://trust.pdpo.ai';

/** Path of an organisation's Trust Centre page, relative to the trust host. */
export function trustCentrePath(slug: string): string {
  return `/p/${slug}`;
}

/** Absolute URL of an organisation's Trust Centre page, from its public slug. */
export function trustCentreUrl(slug: string): string {
  return `${TRUST_CENTRE_URL}${trustCentrePath(slug)}`;
}

/** The signed-in customer portal — everything held about the person, one tap away. */
export const CUSTOMER_PORTAL_URL = 'https://go.pdpo.ai/portal';

/** Path of "request a copy or correction of your data", addressed through the organisation's public portal token. */
export function trustRequestPath(portalToken: string): string {
  return `/dsar/${portalToken}`;
}

/** Path of "report a concern about how your data was handled", addressed through the organisation's public portal token. */
export function trustReportPath(portalToken: string): string {
  return `/report/${portalToken}`;
}

export function trustRequestUrl(portalToken: string): string {
  return `${TRUST_CENTRE_URL}${trustRequestPath(portalToken)}`;
}

export function trustReportUrl(portalToken: string): string {
  return `${TRUST_CENTRE_URL}${trustReportPath(portalToken)}`;
}

export interface TrustLinks {
  /** The privacy notice page; null until the organisation claims a public slug. */
  url: string | null;
  /** Request a copy or correction of your data; null without a portal token. */
  requestUrl: string | null;
  /** Report a concern about how your data was handled; null without a portal token. */
  reportUrl: string | null;
  /** The signed-in customer portal. */
  portalUrl: string;
}

/**
 * The links a customer-facing page offers for one organisation, from the
 * slug and public portal token it already holds. Mirrors public-pages
 * `trustLinks(slug, portalToken)`.
 */
export function trustLinks(slug: string | null | undefined, portalToken: string | null | undefined): TrustLinks {
  return {
    url: slug ? trustCentreUrl(slug) : null,
    requestUrl: portalToken ? trustRequestUrl(portalToken) : null,
    reportUrl: portalToken ? trustReportUrl(portalToken) : null,
    portalUrl: CUSTOMER_PORTAL_URL,
  };
}

const TRUST_HOST_BASES = ['trust.pdpo.ai', 'trust.localhost'];

export type TrustHost = { kind: 'directory' } | { kind: 'centre'; slug: string } | null;

/** Which trust host (if any) the app is being served from. */
export function getTrustHost(): TrustHost {
  const host = window.location.hostname.toLowerCase();
  for (const base of TRUST_HOST_BASES) {
    if (host === base) return { kind: 'directory' };
    if (host.endsWith(`.${base}`)) {
      const slug = host.slice(0, -(base.length + 1));
      if (slug && !slug.includes('.')) return { kind: 'centre', slug };
    }
  }
  return null;
}
